import type { JSX } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AppLayout } from '@/app/app-layout';
import { ProtectedRoute } from '@/app/protected-route';
import { RequireActiveOrganization } from '@/app/require-active-organization';
import { RequirePlatformCapability } from '@/app/require-platform-capability';
import { AcceptInvitationPage } from '@/features/guardian/accept-invitation-page';
import { MyChildrenPage } from '@/features/guardian/my-children-page';
import { AcceptInvitationPage as AcceptOrganizationInvitationPage } from '@/features/membership/accept-invitation-page';
import { SubmissionDetailPage } from '@/features/memorization/submission-detail-page';
import { TargetDetailPage } from '@/features/memorization/target-detail-page';
import { MurajaahQueuePage } from '@/features/murajaah/queue-page';
import { AccountSessionsPage } from '@/pages/account-sessions-page';
import { ClassDetailPage } from '@/pages/class-detail-page';
import { ClassesPage } from '@/pages/classes-page';
import { DashboardPage } from '@/pages/dashboard-page';
import { ForgotPasswordPage } from '@/pages/forgot-password-page';
import { HafalanPage } from '@/pages/hafalan-page';
import { LearningSpaceDetailPage } from '@/pages/learning-space-detail-page';
import { LearningSpacesPage } from '@/pages/learning-spaces-page';
import { LoginPage } from '@/pages/login-page';
import { NotFoundPage } from '@/pages/not-found-page';
import { OrganizationPage } from '@/pages/organization-page';
import { PlatformAdminPage } from '@/pages/platform-admin-page';
import { ProfilePage } from '@/pages/profile-page';
import { RegisterPage } from '@/pages/register-page';
import { ResetPasswordPage } from '@/pages/reset-password-page';
import { StudentDetailPage } from '@/pages/student-detail-page';
import { StudentsPage } from '@/pages/students-page';
import { VerifyEmailPage } from '@/pages/verify-email-page';

/**
 * Centralized route definitions (ADR-026, Decision F1): public routes,
 * one protected subtree, and a catch-all. `/guardian-invitations/accept`
 * and `/organization-invitations/accept` (ADR-027, Phase G-8) are
 * intentionally public - each must work for a user with no organization
 * membership yet (Phase G-0, Job 10; ADR-027, Decision 13). Inside the
 * protected subtree, `/platform` (G-9, Job 6) is a SIBLING of the Active
 * Organization gate, not nested inside it - a Platform Super Admin
 * legitimately has zero organization memberships, so that route is gated
 * only by `RequirePlatformCapability`. Every other tenant-scoped screen
 * still sits behind Active Organization resolution (ADR-026 Addendum,
 * AO-5) exactly as before.
 */
export function AppRouter(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/guardian-invitations/accept" element={<AcceptInvitationPage />} />
      <Route
        path="/organization-invitations/accept"
        element={<AcceptOrganizationInvitationPage />}
      />

      <Route element={<ProtectedRoute />}>
        <Route element={<RequirePlatformCapability capability="organizations:manage" />}>
          <Route path="/platform" element={<PlatformAdminPage />} />
        </Route>
        <Route element={<RequireActiveOrganization />}>
          <Route element={<AppLayout />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/account/sessions" element={<AccountSessionsPage />} />
            <Route path="/hafalan" element={<HafalanPage />} />
            <Route path="/profil" element={<ProfilePage />} />
            <Route path="/students" element={<StudentsPage />} />
            <Route path="/students/:studentId" element={<StudentDetailPage />} />
            <Route path="/students/:studentId/targets/:targetId" element={<TargetDetailPage />} />
            <Route
              path="/students/:studentId/setoran/:submissionId"
              element={<SubmissionDetailPage />}
            />
            <Route path="/murajaah/queue" element={<MurajaahQueuePage />} />
            <Route path="/guardians/me" element={<MyChildrenPage />} />
            <Route path="/organization" element={<OrganizationPage />} />
            <Route path="/learning-spaces" element={<LearningSpacesPage />} />
            <Route path="/learning-spaces/:learningSpaceId" element={<LearningSpaceDetailPage />} />
            <Route path="/classes" element={<ClassesPage />} />
            <Route path="/classes/:classId" element={<ClassDetailPage />} />
          </Route>
        </Route>
      </Route>

      <Route path="/404" element={<NotFoundPage />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
}
